type SizedIterable<T> = { size: number; [Symbol.iterator](): Iterator<T> };

// V8エンジン(64bit、ポインター圧縮あり)上でのメモリ使用量を概算するための各要素のバイト数
const MEMORY_SIZES = {
  POINTER: 4,
  HEAP_NUMBER: 12,
  DOUBLE: 8,
  STRING_HEADER: 12,
  OBJECT_HEADER: 12,
  ARRAY_HEADER: 24,
  UNKNOWN: 128,
} as const satisfies Record<string, number>;

// 配列などのデータの全要素のサイズを計算しないようサンプル抽出する数
const MAX_SAMPLE_COUNT = 100;

const isSmallInteger = (value: number): boolean => {
  // V8エンジンにおける小さい整数(Smi)かどうかを判定する
  return Number.isInteger(value) && -(2 ** 30) <= value && value <= 2 ** 30 - 1;
};

const isSmallIntegerArray = (value: unknown[]): boolean => {
  // 配列の全要素がV8エンジンにおける小さい整数(Smi)かどうかを判定する
  return value.every((item: unknown): boolean => {
    return typeof item === 'number' && isSmallInteger(item);
  });
};

const isNumberArray = (value: unknown[]): value is number[] => {
  return value.every((item: unknown): item is number => {
    return typeof item === 'number';
  });
};

const extractSamples = <T>(
  items: T[] | SizedIterable<T>,
  maxCount: number
): T[] => {
  if (Array.isArray(items)) {
    const count = Math.min(items.length, maxCount);

    // データセットからサンプルを等間隔に抽出する
    return Array.from({ length: count }, (_: unknown, index: number): T => {
      return items[Math.floor((index * items.length) / count)];
    });
  } else {
    const count = Math.min(items.size, maxCount);

    // メモリ効率を考慮して配列に変換せずに、ループ処理で等間隔にサンプルを抽出する
    const samples: T[] = [];
    let index = 0;
    for (const item of items) {
      if (index % Math.ceil(items.size / count) === 0) {
        samples.push(item);
      }
      if (samples.length >= count) {
        // サンプルが必要数に達したらループを終了する
        break;
      }
      index++;
    }
    return samples;
  }
};

const estimateMemorySize = (value: unknown): number => {
  if (value === null || value === undefined || typeof value === 'boolean') {
    // null、undefined、真偽値はグローバルな固定の値への参照しか持たないため、サイズは0とみなす
    return 0;
  }
  if (typeof value === 'number') {
    // 小さい整数(Smi)はポインターに直接埋め込まれ追加のメモリ確保が行われないため、サイズは0とみなす
    if (isSmallInteger(value)) {
      return 0;
    }
    return MEMORY_SIZES.HEAP_NUMBER;
  }
  if (typeof value === 'string') {
    // 文字列が全てASCII文字であると仮定して、サイズは1文字1バイトで計算する
    return MEMORY_SIZES.STRING_HEADER + value.length;
  }
  if (
    typeof value === 'function' ||
    typeof value === 'symbol' ||
    typeof value === 'bigint'
  ) {
    // 関数やシンボルなどのサイズが不明な値は、サイズは仮に大きめの値とする
    return MEMORY_SIZES.UNKNOWN;
  }
  if (Array.isArray(value)) {
    const samples = extractSamples(value, MAX_SAMPLE_COUNT);

    if (isSmallIntegerArray(samples)) {
      // 小さい整数(Smi)のみの配列は、ポインターに直接埋め込まれる
      return MEMORY_SIZES.ARRAY_HEADER + value.length * MEMORY_SIZES.POINTER;
    } else if (isNumberArray(samples)) {
      // 数値のみの配列は、数値がポインターを介さず直接並べて格納される
      return MEMORY_SIZES.ARRAY_HEADER + value.length * MEMORY_SIZES.DOUBLE;
    } else {
      const samplesSize = samples.reduce(
        (size: number, item: unknown): number => {
          return size + MEMORY_SIZES.POINTER + estimateMemorySize(item);
        },
        0
      );
      return (
        MEMORY_SIZES.ARRAY_HEADER +
        samplesSize * (value.length / Math.max(samples.length, 1))
      );
    }
  }
  if (value instanceof Set) {
    const samples = extractSamples(value, MAX_SAMPLE_COUNT);
    const samplesSize = samples.reduce(
      (size: number, item: unknown): number => {
        return size + MEMORY_SIZES.POINTER + estimateMemorySize(item);
      },
      0
    );
    const itemsSize = samplesSize * (value.size / Math.max(samples.length, 1));

    // Setは値のポインターに、要素ごとにハッシュテーブルのバケットとチェーンの分のサイズを加える
    return (
      MEMORY_SIZES.OBJECT_HEADER +
      value.size * 2 * MEMORY_SIZES.POINTER +
      itemsSize
    );
  }
  if (value instanceof Map) {
    const samples = extractSamples(value, MAX_SAMPLE_COUNT);
    const samplesSize = samples.reduce(
      (size: number, item: [unknown, unknown]): number => {
        return (
          size +
          MEMORY_SIZES.POINTER * 2 +
          estimateMemorySize(item[0]) +
          estimateMemorySize(item[1])
        );
      },
      0
    );
    const itemsSize = samplesSize * (value.size / Math.max(samples.length, 1));

    // Mapはキーと値のポインターに、エントリごとにハッシュテーブルのバケットとチェーンの分のサイズを加える
    return (
      MEMORY_SIZES.OBJECT_HEADER +
      value.size * 2 * MEMORY_SIZES.POINTER +
      itemsSize
    );
  }
  // オブジェクトについては、プロパティ名は共有で管理されるため値についてのみ計算する
  const samples = extractSamples(Object.values(value), MAX_SAMPLE_COUNT);
  const samplesSize = samples.reduce((size: number, item: unknown): number => {
    return size + MEMORY_SIZES.POINTER + estimateMemorySize(item);
  }, 0);
  return (
    MEMORY_SIZES.OBJECT_HEADER +
    samplesSize * (Object.values(value).length / Math.max(samples.length, 1))
  );
};

export { estimateMemorySize };
